import type { IconGlyph, IconProps as GlyphProps } from "@/icons";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type CSSProperties } from "react";
import { useSurface, type SurfaceKind } from "./Surface";

/** Size and tone classes of `Icon`; sizes are rem-based Tailwind boxes, never pixel props. */
export const iconVariants = cva("shrink-0", {
  variants: {
    size: {
      "2xs": "h-3 w-3",
      xs: "h-3.5 w-3.5",
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-6 w-6",
      xl: "h-7 w-7",
      "2xl": "h-8 w-8",
      "3xl": "h-10 w-10",
      "4xl": "h-12 w-12",
      "5xl": "h-16 w-16",
      "6xl": "h-20 w-20",
      custom: "h-[var(--icon-size)] w-[var(--icon-size)]",
    },
    tone: {
      current: "",
      primary: "text-primary",
      secondary: "text-secondary",
      muted: "text-card-text/75",
      subtle: "text-card-text/50",
      danger: "text-danger",
      success: "text-success",
      warning: "text-warning",
      "on-primary": "text-primary-foreground",
      "on-media": "text-on-media",
      "game-accent": "text-game-gold-deep",
    },
  },
  defaultVariants: { size: "md", tone: "current" },
});

/** Named icon sizes accepted by `Icon` (`custom` needs `customSize`). */
export type IconSize = Exclude<NonNullable<VariantProps<typeof iconVariants>["size"]>, "custom">;

/** Named icon tones accepted by `Icon`. */
export type IconTone = NonNullable<VariantProps<typeof iconVariants>["tone"]>;

const SURFACE_TONE: Partial<Record<SurfaceKind, Partial<Record<IconTone, string>>>> = {
  page: { muted: "text-page-text/75", subtle: "text-page-text/50" },
  game: { muted: "text-game-muted", subtle: "text-game-ink/50" },
  night: { muted: "text-night-ink/80", subtle: "text-night-ink/60" },
};

type IconSizing = { size?: IconSize; customSize?: never } | { size: "custom"; customSize: number };

/** Props of `Icon`; `label` makes it meaningful (role=img); `size="custom"` takes `customSize` in rem. */
export type IconProps = Omit<VariantProps<typeof iconVariants>, "size"> &
  IconSizing &
  Omit<GlyphProps, "size" | "color" | "alt" | "className" | "aria-hidden" | "aria-label" | "role"> & {
    as: IconGlyph;
    label?: string;
    className?: string;
  };

/** Renders a Phosphor glyph at a token size and tone; decorative (aria-hidden) unless `label` is set. */
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { as: Glyph, size, customSize, tone, label, className, style, ...rest },
  ref,
) {
  const surface = useSurface();
  const surfaceTone = tone ? SURFACE_TONE[surface]?.[tone] : undefined;
  const sizing: CSSProperties | undefined =
    size === "custom" ? ({ "--icon-size": `${customSize}rem`, ...style } as CSSProperties) : style;
  return (
    <Glyph
      {...rest}
      ref={ref}
      style={sizing}
      focusable="false"
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(iconVariants({ size, tone }), surfaceTone, className)}
    />
  );
});
